import { useEffect, useState, useCallback } from 'react';

export default function GlitchEffect() {
  const [glitchActive, setGlitchActive] = useState(false);
  const [glitchIntensity, setGlitchIntensity] = useState(0);
  const [flickerActive, setFlickerActive] = useState(false);
  const [dataCorruption, setDataCorruption] = useState(false);
  const [extremeGlitch, setExtremeGlitch] = useState(false);
  
  // Function to randomly trigger glitch effects
  const triggerGlitch = useCallback(() => {
    const intensity = Math.random();
    setGlitchIntensity(intensity);
    setGlitchActive(true);
    
    // Random chance for different types of glitches
    if (Math.random() > 0.6) {
      setFlickerActive(true);
      setTimeout(() => setFlickerActive(false), Math.random() * 300 + 50);
    }
    
    if (Math.random() > 0.8) {
      setDataCorruption(true);
      setTimeout(() => setDataCorruption(false), Math.random() * 200 + 100);
    }
    
    if (Math.random() > 0.9) {
      setExtremeGlitch(true);
      setTimeout(() => setExtremeGlitch(false), Math.random() * 500 + 200);
    }
    
    // Reset glitch after a short duration
    setTimeout(() => {
      setGlitchActive(false);
    }, Math.random() * 300 + 100);
  }, []);
  
  // Set up glitch intervals - more frequent and aggressive
  useEffect(() => {
    // Initial glitch on component mount
    triggerGlitch();
    
    // More frequent minor glitches
    const minorGlitchInterval = setInterval(() => {
      if (Math.random() > 0.4) { // 60% chance to trigger a minor glitch
        triggerGlitch();
      }
    }, 400);
    
    // Major glitch effect (more frequent)
    const majorGlitchInterval = setInterval(() => {
      if (Math.random() > 0.3) { // 70% chance to trigger a major glitch
        // Trigger multiple glitches in rapid sequence
        triggerGlitch();
        setTimeout(triggerGlitch, 50);
        setTimeout(triggerGlitch, 150);
        setTimeout(triggerGlitch, 250);
      }
    }, 1500);
    
    // Extreme glitch storms
    const stormInterval = setInterval(() => {
      if (Math.random() > 0.85) { // 15% chance for glitch storm
        for (let i = 0; i < 8; i++) {
          setTimeout(triggerGlitch, i * 100);
        }
      }
    }, 5000);
    
    return () => {
      clearInterval(minorGlitchInterval);
      clearInterval(majorGlitchInterval);
      clearInterval(stormInterval);
    };
  }, [triggerGlitch]);

  // Calculate transform values based on glitch intensity
  const getGlitchTransform = (layer: number) => {
    const maxOffset = glitchIntensity * 20; // Increased max offset
    const xOffset = (Math.random() * 2 - 1) * maxOffset;
    const yOffset = (Math.random() * 2 - 1) * maxOffset;
    const modifier = layer === 1 ? 1 : -1;
    const scale = 0.98 + Math.random() * 0.04;
    const skew = (Math.random() * 2 - 1) * glitchIntensity * 3;
    
    return `translate(${xOffset * modifier}px, ${yOffset}px) scale(${scale}) skew(${skew}deg)`;
  };

  return (
    <div className="relative w-[768px] h-[768px] overflow-hidden flex items-center justify-center" data-name="Logo Bug Intense Glitch">
      {/* Portrait background image */}
      <div
        className={`absolute inset-0 bg-center bg-cover bg-no-repeat
          ${glitchActive && glitchIntensity > 0.7 ? 'animate-pixel-displacement scale-[1.02] brightness-125' : ''}
          ${extremeGlitch ? 'animate-intense-glitch' : ''}
          ${dataCorruption ? 'animate-data-corruption' : ''}`}
        style={{
          backgroundImage: `url('/mother-logo.png')`,
          transition: 'transform 0.05s, filter 0.05s',
          filter: glitchActive ? `
            contrast(${1 + glitchIntensity})
            brightness(${1 + glitchIntensity * 0.5})
            hue-rotate(${glitchIntensity * 90 + 80}deg)
            saturate(${1 + glitchIntensity})
            sepia(0.5)
          ` : 'sepia(0.5) hue-rotate(80deg) saturate(2) brightness(0.8)'
        }}
      />
      
      {/* Color overlay for green tint effect */}
      <div
        className={`absolute inset-0 bg-green-500/20 mix-blend-overlay
          ${glitchActive && glitchIntensity > 0.7 ? 'animate-pixel-displacement scale-[1.02]' : ''}
          ${extremeGlitch ? 'animate-intense-glitch' : ''}
          ${dataCorruption ? 'animate-data-corruption' : ''}`}
        style={{
          transition: 'transform 0.05s, filter 0.05s'
        }}
      />
      
      {/* Base musical note - perfectly centered */}
      <div 
        className={`
          [text-shadow:rgba(0,0,0,0.25)_0px_4px_4px] absolute 
          font-['Zilla_Slab_Highlight:Bold',_sans-serif] font-bold leading-[0] 
          text-[400px] text-[rgba(211,222,208,0.6)] 
          text-center flex items-center justify-center
          ${flickerActive ? 'electric-flicker' : ''}
          ${extremeGlitch ? 'animate-rgb-split' : ''}
        `}
        style={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'auto',
          height: 'auto'
        }}
      >
        <span>♪</span>
      </div>
      
      {/* Cyan glitch layer - centered */}
      <div 
        className={`
          absolute font-['Zilla_Slab_Highlight:Bold',_sans-serif] font-bold leading-[0] 
          text-[400px] text-cyan-500/60 text-center flex items-center justify-center
          mix-blend-screen animate-glitch-layer-1
          ${glitchActive ? 'opacity-80' : 'opacity-0'}
          ${extremeGlitch ? 'animate-intense-glitch' : ''}
        `}
        style={{
          left: '50%',
          top: '50%',
          transform: glitchActive ? 
            `translate(-50%, -50%) ${getGlitchTransform(1)}` : 
            'translate(-50%, -50%)',
          filter: glitchActive ? `blur(${glitchIntensity * 3}px) contrast(${1 + glitchIntensity})` : 'none',
          transition: 'opacity 0.05s'
        }}
      >
        <span>♪</span>
      </div>
      
      {/* Magenta glitch layer - centered */}
      <div 
        className={`
          absolute font-['Zilla_Slab_Highlight:Bold',_sans-serif] font-bold leading-[0] 
          text-[400px] text-pink-500/60 text-center flex items-center justify-center
          mix-blend-screen animate-glitch-layer-2
          ${glitchActive ? 'opacity-80' : 'opacity-0'}
          ${extremeGlitch ? 'animate-intense-glitch' : ''}
        `}
        style={{
          left: '50%',
          top: '50%',
          transform: glitchActive ? 
            `translate(-50%, -50%) ${getGlitchTransform(2)}` : 
            'translate(-50%, -50%)',
          filter: glitchActive ? `blur(${glitchIntensity * 2}px) saturate(${1 + glitchIntensity})` : 'none',
          transition: 'opacity 0.05s'
        }}
      >
        <span>♪</span>
      </div>
      
      {/* Yellow glitch layer - centered */}
      {glitchActive && glitchIntensity > 0.5 && (
        <div 
          className={`
            absolute font-['Zilla_Slab_Highlight:Bold',_sans-serif] font-bold leading-[0] 
            text-[400px] text-yellow-400/50 text-center flex items-center justify-center
            mix-blend-screen
            ${extremeGlitch ? 'animate-data-corruption' : ''}
          `}
          style={{
            left: '50%',
            top: '50%',
            transform: `translate(-50%, -50%) ${getGlitchTransform(3)}`,
            opacity: glitchIntensity > 0.7 ? 0.8 : 0.5,
            filter: `blur(${glitchIntensity * 4}px) hue-rotate(${glitchIntensity * 180}deg)`
          }}
        >
          <span>♪</span>
        </div>
      )}
      
      {/* Green glitch layer - centered (extreme glitch only) */}
      {extremeGlitch && (
        <div 
          className="
            absolute font-['Zilla_Slab_Highlight:Bold',_sans-serif] font-bold leading-[0] 
            text-[400px] text-green-400/60 text-center flex items-center justify-center
            mix-blend-screen animate-intense-glitch
          "
          style={{
            left: '50%',
            top: '50%',
            transform: `translate(-50%, -50%) ${getGlitchTransform(4)}`,
            opacity: 0.7,
            filter: `blur(${glitchIntensity * 5}px) contrast(2)`
          }}
        >
          <span>♪</span>
        </div>
      )}
      
      {/* Enhanced noise overlay */}
      <div 
        className={`
          absolute inset-0 bg-[url('data:image/png;base64,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')] 
          opacity-30 mix-blend-overlay pointer-events-none
          ${glitchActive ? 'animate-noise' : ''}
          ${extremeGlitch ? 'scale-105 animate-intense-glitch' : ''}
        `}
        style={{
          filter: dataCorruption ? 'contrast(3) brightness(2) hue-rotate(180deg)' : 'none'
        }}
      />
      
      {/* Digital artifacts */}
      {dataCorruption && (
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 20 }, (_, i) => (
            <div
              key={i}
              className="absolute bg-white animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${2 + Math.random() * 10}px`,
                height: `${1 + Math.random() * 3}px`,
                opacity: Math.random() * 0.8,
                transform: `rotate(${Math.random() * 360}deg)`,
              }}
            />
          ))}
        </div>
      )}
      
      {/* Scanline effect - enhanced */}
      <div 
        className={`absolute inset-0 bg-gradient-to-b from-transparent via-white/5 to-transparent 
          bg-[length:100%_2px] pointer-events-none opacity-40
          ${extremeGlitch ? 'animate-pulse bg-[length:100%_1px]' : ''}`} 
      />
      
      {/* Edge distortion - more aggressive */}
      {glitchActive && glitchIntensity > 0.6 && (
        <div className="absolute inset-0 pointer-events-none" 
          style={{
            boxShadow: `
              inset 0 0 50px rgba(0, 255, 255, ${glitchIntensity * 0.8}),
              inset 0 0 50px rgba(255, 0, 255, ${glitchIntensity * 0.6}),
              inset 0 0 100px rgba(255, 255, 0, ${glitchIntensity * 0.4})
            `,
            opacity: glitchIntensity - 0.4
          }}
        />
      )}
    </div>
  );
}